import * as React from "react";
import {
  Grid,
  TextField,
  Typography,
  Paper,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
} from "@material-ui/core";
import PersonIcon from "@material-ui/icons/Person";
import { IPatientDetails } from "../types/PrescriptionIngestionTypes";
import { IFieldErrors } from "../utils/ValidationUtils";
import { COLORS } from "../styles/theme";

interface IProps {
  patientDetails: IPatientDetails;
  onFieldChange: (field: keyof IPatientDetails, value: string) => void;
  fieldErrors: IFieldErrors;
  addresses?: any[];
  onAddressSelect?: (address: any, index: number) => void;
  showAddressDropdown?: boolean;
}

const PatientDetailsForm: React.FC<IProps> = ({
  patientDetails,
  onFieldChange,
  fieldErrors,
  addresses,
  onAddressSelect,
  showAddressDropdown = false,
}) => {
  const [selectedAddressIndex, setSelectedAddressIndex] =
    React.useState<string>("");
  const [isOverrideEnabled, setIsOverrideEnabled] =
    React.useState<boolean>(false);
  const [showOverrideWarning, setShowOverrideWarning] =
    React.useState<boolean>(false);

  const handleAddressChange = (value: string) => {
    if (value === "") {
      return;
    }
    const index = parseInt(value, 10);
    const selectedAddr = addresses?.[index];
    if (selectedAddr && onAddressSelect) {
      setSelectedAddressIndex(value);
      onAddressSelect(selectedAddr, index);
    }
  };

  const handleOverrideClick = () => {
    setShowOverrideWarning(true);
  };

  const handleConfirmOverride = () => {
    setIsOverrideEnabled(true);
    setShowOverrideWarning(false);
  };

  const handleCancelOverride = () => {
    setShowOverrideWarning(false);
  };

  return (
    <Grid item xs={12}>
      <Paper
        elevation={2}
        style={{
          padding: 24,
          borderRadius: 8,
          backgroundColor: "#f5f5f5",
          border: "1px solid #e0e0e0",
        }}
      >
        <Typography
          variant="h6"
          gutterBottom
          style={{
            color: COLORS.textPrimary,
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <PersonIcon style={{ color: "#546e7a" }} />
          Patient Details
        </Typography>
        <Typography
          variant="caption"
          color="textSecondary"
          style={{ display: "block", marginBottom: 16 }}
        >
          Configurable
        </Typography>
        <Divider style={{ marginBottom: 20 }} />

        <Grid container spacing={3}>
          {showAddressDropdown && addresses && addresses.length > 0 && (
            <>
              <Grid item xs={12}>
                <FormControl
                  fullWidth
                  variant="outlined"
                  required
                  error={!!fieldErrors.addressLine1}
                >
                  <InputLabel>Select Address *</InputLabel>
                  <Select
                    value={selectedAddressIndex}
                    onChange={(e) =>
                      handleAddressChange(e.target.value as string)
                    }
                    label="Select Address *"
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 400,
                        },
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em>Select an address</em>
                    </MenuItem>
                    {addresses.map((address, index) => {
                      const displayText = `${address.stateName}, ${address.city} ${address.zipCode}`;
                      return (
                        <MenuItem key={index} value={index.toString()}>
                          <span style={{ fontStyle: "italic" }}>
                            {displayText}
                          </span>
                        </MenuItem>
                      );
                    })}
                  </Select>
                  {fieldErrors.addressLine1 && (
                    <Typography
                      variant="caption"
                      color="error"
                      style={{ marginTop: 4, marginLeft: 14 }}
                    >
                      Please select an address
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              {!isOverrideEnabled && (
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleOverrideClick}
                    style={{
                      backgroundColor: COLORS.orange,
                      color: "black",
                    }}
                  >
                    Override Auto Generated Patient Details
                  </Button>
                </Grid>
              )}

              {showOverrideWarning && (
                <Grid item xs={12}>
                  <Paper
                    style={{
                      padding: 16,
                      backgroundColor: "#fff3e0",
                      border: "2px solid #ff9800",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      style={{ fontWeight: "bold", color: "#e65100" }}
                      gutterBottom
                    >
                      Warning: Override Auto Generated Patient Details
                    </Typography>
                    <Typography variant="body2" paragraph>
                      <strong>
                        Incorrect data will cause the prescription to not ingest
                        as expected.
                      </strong>{" "}
                      Please recheck and proceed with caution.
                    </Typography>
                    <Typography
                      variant="body2"
                      paragraph
                      style={{ fontStyle: "italic" }}
                    >
                      Only override when you need to create a customer with
                      specific demographics (custom name, DOB, gender, phone,
                      etc.) or attach a Rx to an exiting customer profile.
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleConfirmOverride}
                          style={{
                            backgroundColor: COLORS.orange,
                            color: "black",
                          }}
                        >
                          I Understand, Enable Override
                        </Button>
                      </Grid>
                      <Grid item>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={handleCancelOverride}
                          style={{
                            borderColor: COLORS.orange,
                            color: "black",
                          }}
                        >
                          Cancel
                        </Button>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              )}
            </>
          )}

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="First Name"
              variant="outlined"
              required
              value={patientDetails.firstName}
              onChange={(e) => onFieldChange("firstName", e.target.value)}
              error={!!fieldErrors.firstName}
              helperText={fieldErrors.firstName}
              InputProps={{
                readOnly: showAddressDropdown && !isOverrideEnabled,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Last Name"
              variant="outlined"
              required
              value={patientDetails.lastName}
              onChange={(e) => onFieldChange("lastName", e.target.value)}
              error={!!fieldErrors.lastName}
              helperText={fieldErrors.lastName}
              InputProps={{
                readOnly: showAddressDropdown && !isOverrideEnabled,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl
              fullWidth
              variant="outlined"
              error={!!fieldErrors.gender}
              disabled={showAddressDropdown && !isOverrideEnabled}
            >
              <InputLabel>Gender</InputLabel>
              <Select
                value={patientDetails.gender}
                onChange={(e) =>
                  onFieldChange("gender", e.target.value as string)
                }
                label="Gender"
              >
                <MenuItem value="M">M - Male</MenuItem>
                <MenuItem value="F">F - Female</MenuItem>
                <MenuItem value="U">U - Unknown</MenuItem>
              </Select>
              {fieldErrors.gender && (
                <Typography
                  variant="caption"
                  color="error"
                  style={{ marginTop: 4, marginLeft: 14 }}
                >
                  {fieldErrors.gender}
                </Typography>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Date of Birth (YYYY-MM-DD)"
              variant="outlined"
              required
              value={patientDetails.dateOfBirth}
              onChange={(e) => onFieldChange("dateOfBirth", e.target.value)}
              error={!!fieldErrors.dateOfBirth}
              helperText={fieldErrors.dateOfBirth}
              InputProps={{
                readOnly: showAddressDropdown && !isOverrideEnabled,
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Address Line 1"
              variant="outlined"
              required
              value={patientDetails.addressLine1}
              onChange={(e) => onFieldChange("addressLine1", e.target.value)}
              error={!!fieldErrors.addressLine1}
              helperText={fieldErrors.addressLine1}
              InputProps={{
                readOnly: showAddressDropdown && !isOverrideEnabled,
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Address Line 2"
              variant="outlined"
              required
              value={patientDetails.addressLine2}
              onChange={(e) => onFieldChange("addressLine2", e.target.value)}
              error={!!fieldErrors.addressLine2}
              helperText={fieldErrors.addressLine2}
              InputProps={{
                readOnly: showAddressDropdown && !isOverrideEnabled,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="City"
              variant="outlined"
              required
              value={patientDetails.city}
              onChange={(e) => onFieldChange("city", e.target.value)}
              error={!!fieldErrors.city}
              helperText={fieldErrors.city}
              InputProps={{
                readOnly: showAddressDropdown && !isOverrideEnabled,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={3} md={4}>
            <TextField
              fullWidth
              label="State"
              variant="outlined"
              required
              value={patientDetails.state}
              onChange={(e) => onFieldChange("state", e.target.value)}
              error={!!fieldErrors.state}
              helperText={fieldErrors.state}
              InputProps={{
                readOnly: showAddressDropdown && !isOverrideEnabled,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={3} md={4}>
            <TextField
              fullWidth
              label="Zip Code"
              variant="outlined"
              required
              value={patientDetails.zipCode}
              onChange={(e) => onFieldChange("zipCode", e.target.value)}
              error={!!fieldErrors.zipCode}
              helperText={fieldErrors.zipCode}
              InputProps={{
                readOnly: showAddressDropdown && !isOverrideEnabled,
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Phone Number"
              variant="outlined"
              required
              value={patientDetails.phoneNumber}
              onChange={(e) => onFieldChange("phoneNumber", e.target.value)}
              error={!!fieldErrors.phoneNumber}
              helperText={fieldErrors.phoneNumber}
            />
          </Grid>
        </Grid>
      </Paper>
    </Grid>
  );
};

export default PatientDetailsForm;
